<script setup lang="ts">
import { InfoCircle } from "@iconoir/vue";
import { useFormat } from "~/composables/use-format";

defineOptions({ name: "MetricCard" });

interface Props {
  label: string;
  value: string;
  // tooltip explaining what the metric counts
  hint?: string;
  secondary?: string;
  size?: "medium" | "large";
  intent?: "neutral" | "positive" | "negative";
  delta?: number | null;
  // percent expects the delta already in percent units (12.5 = 12.5%)
  deltaFormat?: "percent" | "integer";
  deltaLabel?: string;
  // for metrics where going up is bad, e.g. churn
  deltaInverted?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hint: "",
  secondary: "",
  size: "medium",
  intent: "neutral",
  delta: null,
  deltaFormat: "percent",
  deltaLabel: "",
  deltaInverted: false,
  loading: false,
});

const { n } = useFormat();

const deltaUp = computed(() => (props.delta ?? 0) > 0);
const deltaText = computed(() =>
  props.deltaFormat === "percent"
    ? n(Math.abs(props.delta ?? 0) / 100, "percent")
    : n(Math.abs(props.delta ?? 0), "integer"),
);
</script>

<template>
  <Card class="metric-card flex flex-col">
    <div class="flex items-center gap-2xsmall">
      <span class="text-body-tertiary content-neutral-medium truncate">{{ label }}</span>
      <!-- opens downward: the top row of cards sits right under the sticky topbar -->
      <Tooltip v-if="hint" :label="hint" side="bottom" class="metric-card__hint">
        <InfoCircle />
      </Tooltip>
    </div>

    <div
      class="metric-card__value"
      :class="[
        size === 'large' ? 'h2-primary lg:h1-primary' : 'h3-primary lg:h2-primary',
        loading
          ? 'content-neutral-medium'
          : {
              'content-positive': intent === 'positive',
              'content-negative': intent === 'negative',
            },
      ]"
    >
      <template v-if="loading">—</template>
      <template v-else>
        {{ value }}
        <span v-if="secondary" class="text-body-small-tertiary content-neutral-medium">{{ secondary }}</span>
      </template>
    </div>

    <!-- the comparison row always holds its place, so a null delta keeps the cards aligned -->
    <span v-if="deltaLabel" class="text-body-small-secondary flex items-center gap-2xsmall">
      <span v-if="!loading && delta" :class="deltaUp !== deltaInverted ? 'content-positive' : 'content-negative'">
        {{ deltaUp ? "↑" : "↓" }} {{ deltaText }}
      </span>
      <span v-else class="content-neutral-medium">—</span>
      <span class="text-body-small-tertiary content-neutral-medium">{{ deltaLabel }}</span>
    </span>
  </Card>
</template>

<style scoped lang="scss">
.metric-card {
  gap: token("gap-xsmall");

  &__hint {
    display: inline-flex;
    color: token("color-content-neutral-medium");

    svg {
      width: token("icon-size-small");
      height: token("icon-size-small");
    }
  }

  &__value {
    // digits line up across a row of cards
    font-variant-numeric: tabular-nums;
  }
}
</style>
